import { z } from 'zod';

/**
 * #139 / #141 — Unified search surface (geocoder + entity search).
 *
 * One endpoint, two result groups:
 *   - PLACES   — a pass-through to the NRCan GeoNames-Canada geocoder
 *                (keyless), each carrying a result bbox so the client can fly
 *                to the Google-familiar zoom.
 *   - ENTITIES — operator-domain objects (cameras, response assets, active
 *                fires, communities, incidents) matched by name. Each carries
 *                the lat/lon and the minimum the client needs to route through
 *                the EXISTING deep-link handlers (focusCamera / asset select /
 *                flyToIncident / community select).
 */

/** The operator-domain object kinds the entity search spans. The client maps
 *  each to an existing selection/deep-link handler. */
export const SEARCH_ENTITY_KINDS = [
  'camera',
  'response_asset',
  'active_fire',
  'community',
  'incident',
] as const;

export const SearchEntityKindSchema = z.enum(SEARCH_ENTITY_KINDS);
export type SearchEntityKind = z.infer<typeof SearchEntityKindSchema>;

/** A geocoded place from the NRCan GeoNames-Canada service. `bbox` is the
 *  service's result extent [west, south, east, north]; null for results that
 *  carry only a point (e.g. interpolated street centroids), in which case the
 *  client flies to the point at a default altitude. */
export const SearchPlaceSchema = z.object({
  title: z.string(),
  lat: z.number(),
  lon: z.number(),
  bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]).nullable(),
});
export type SearchPlace = z.infer<typeof SearchPlaceSchema>;

/** A matched operator-domain entity. `detail` is a short sublabel (source,
 *  asset class, fire stage, community type, incident number) for disambiguation.
 *  `source_ref` carries an optional linked-record pointer (currently used by
 *  incidents to route to their originating active fire). */
export const SearchEntitySchema = z.object({
  kind: SearchEntityKindSchema,
  id: z.string(),
  name: z.string(),
  lat: z.number(),
  lon: z.number(),
  detail: z.string().nullable(),
  source_ref: z
    .object({ kind: SearchEntityKindSchema, id: z.string() })
    .nullable()
    .optional(),
});
export type SearchEntity = z.infer<typeof SearchEntitySchema>;

export const SearchResponseSchema = z.object({
  query: z.string(),
  places: z.array(SearchPlaceSchema),
  entities: z.array(SearchEntitySchema),
});
export type SearchResponse = z.infer<typeof SearchResponseSchema>;
